import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  FileText, 
  Trash2, 
  Copy, 
  Check, 
  AlertCircle, 
  ArrowLeft,
  Download,
  Calendar,
  HardDrive,
  Link as LinkIcon,
  ExternalLink
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CPF do administrador autorizado
const ADMIN_CPF = '00000000000';

const AdminCatalogos = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [leiloes, setLeiloes] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // ID do leilão em upload
  const [error, setError] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(null);

  // Verifica se o usuário é o admin
  const isAdmin = user?.cpf?.replace(/\D/g, '') === ADMIN_CPF;

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carregar leilões
      const leiloesResponse = await axios.get(`${API_URL}/api/hybrid/leiloes`);
      const leiloesData = leiloesResponse.data?.data || leiloesResponse.data || [];
      const leiloesAtivos = leiloesData
        .filter(l => l.ATIVOX === 'S')
        .sort((a, b) => new Date(a.DATLEI) - new Date(b.DATLEI));
      setLeiloes(leiloesAtivos);
      
      // Carregar catálogos existentes
      const catalogosResponse = await axios.get(`${API_URL}/api/admin/catalogos`);
      setCatalogos(catalogosResponse.data.catalogos || []);
      
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!isAdmin) {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta área.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }
    
    carregarDados();
  }, [user, isAdmin, navigate, toast, carregarDados]);

  const handleUpload = async (event, leilaoId, leilaoNome) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: "Arquivo inválido",
        description: "Apenas arquivos PDF são permitidos.",
        variant: "destructive",
      });
      return;
    }

    setUploading(leilaoId);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-catalogo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // URL do S3 retornada diretamente pelo backend
        const s3Url = response.data.url;
        
        // Copiar URL do S3 para o clipboard
        try {
          await navigator.clipboard.writeText(s3Url);
          setCopiedUrl(leilaoId);
          setTimeout(() => setCopiedUrl(null), 3000);
        } catch (clipErr) {
          console.error('Erro ao copiar:', clipErr);
        }
        
        toast({
          title: "Upload realizado!",
          description: (
            <div>
              <p>Arquivo enviado para o S3 com sucesso!</p>
              <p className="text-sm font-medium mt-2">URL para colar no sistema Delphi:</p>
              <p className="font-mono text-xs mt-1 bg-green-100 p-2 rounded break-all border border-green-300">{s3Url}</p>
              <p className="text-green-600 font-medium mt-1">✓ Link copiado para a área de transferência!</p>
            </div>
          ),
          duration: 10000,
        });
        
        carregarDados();
      }
    } catch (err) {
      console.error('Erro no upload:', err);
      const message = err.response?.data?.detail || 'Erro ao fazer upload do arquivo';
      setError(message);
      toast({
        title: "Erro no upload",
        description: message,
        variant: "destructive",
      });
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${filename}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/catalogos/${filename}`);
      toast({
        title: "Arquivo excluído",
        description: "O catálogo foi removido com sucesso.",
      });
      carregarDados();
    } catch (err) {
      console.error('Erro ao deletar:', err);
      toast({
        title: "Erro ao excluir",
        description: err.response?.data?.detail || 'Erro ao excluir arquivo',
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async (url) => {
    // URL já é do S3, copia diretamente
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      toast({
        title: "Link copiado!",
        description: (
          <div>
            <p>Cole no campo URL_CATALOGO do leilão no sistema Delphi.</p>
            <p className="font-mono text-xs mt-1 bg-gray-100 p-1 rounded break-all">{url}</p>
          </div>
        ),
      });
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <h1 className="text-3xl font-bold text-[rgb(25,42,86)]">
            Gerenciamento de Catálogos
          </h1>
          <p className="text-gray-600 mt-2">
            Selecione um leilão, faça upload do catálogo PDF e o link será copiado automaticamente.
          </p>
        </div>

        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {/* Lista de Leilões para Upload */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-[rgb(25,42,86)]">
              <Calendar className="h-5 w-5 mr-2" />
              Leilões Ativos - Upload de Catálogo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(25,42,86)] mx-auto"></div>
                <p className="text-gray-500 mt-2">Carregando...</p>
              </div>
            ) : leiloes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum leilão ativo encontrado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {leiloes.map((leilao) => (
                  <div
                    key={leilao.ID}
                    className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-[rgb(25,42,86)] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[rgb(25,42,86)] truncate">
                        {leilao.LEILAO}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(leilao.DATLEI)}
                        </span>
                        <span>ID: {leilao.ID}</span>
                        {leilao.URL_CATALOGO && (
                          <span className="flex items-center text-green-600">
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Catálogo vinculado
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <label className="cursor-pointer">
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleUpload(e, leilao.ID, leilao.LEILAO)}
                          className="hidden"
                          disabled={uploading === leilao.ID}
                        />
                        <Button
                          className={`${
                            copiedUrl === leilao.ID 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90'
                          }`}
                          disabled={uploading === leilao.ID}
                          asChild
                        >
                          <span>
                            {uploading === leilao.ID ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Enviando...
                              </>
                            ) : copiedUrl === leilao.ID ? (
                              <>
                                <Check className="h-4 w-4 mr-2" />
                                Link Copiado!
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Catálogo
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lista de Catálogos Existentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-[rgb(25,42,86)]">
              <FileText className="h-5 w-5 mr-2" />
              Catálogos Enviados ({catalogos.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {catalogos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum catálogo enviado ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {catalogos.map((catalogo) => {
                  // URL já é do S3, usa diretamente
                  const s3Url = catalogo.url;
                  return (
                  <div
                    key={catalogo.filename}
                    className="flex flex-col p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="bg-red-100 p-2 rounded">
                          <FileText className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate text-sm">
                            {catalogo.original_name || catalogo.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(catalogo.size)} • {formatDate(catalogo.created)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(s3Url)}
                          className="text-[rgb(25,42,86)] border-[rgb(25,42,86)]"
                          title="Copiar URL"
                        >
                          {copiedUrl === s3Url ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(s3Url, '_blank')}
                          className="text-green-600 border-green-600"
                          title="Abrir/Testar"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(catalogo.filename)}
                          className="text-red-600 border-red-600"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* URL do S3 para copiar */}
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <p className="text-xs text-green-700 font-medium mb-1">URL para colar no sistema Delphi:</p>
                      <p className="text-xs font-mono text-green-900 break-all select-all">
                        {s3Url}
                      </p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instruções */}
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              Como usar:
            </h3>
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>Clique em <strong>"Upload Catálogo"</strong> no leilão desejado</li>
              <li>Selecione o arquivo PDF do catálogo</li>
              <li>O link será <strong>copiado automaticamente</strong> para a área de transferência</li>
              <li>No sistema interno, cole o link no campo <strong>URL_CATALOGO</strong> do leilão</li>
              <li>Salve as alterações no sistema interno</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCatalogos;
